import { useRuntimeVariables } from 'app-kit'
import { AuthedAxiosRequests, useGeckoAuth } from 'features/auth'
import {
  BUCKETED_FETCH_BUDGET,
  TWO_PHASE_LOAD,
} from 'features/timeSeries/config/constants'
import {
  perfFetchDispatched,
  perfFetchFirstByte,
  perfFetchLastByte,
} from 'features/timeSeries/perf/processDataPerf'
import {
  aggregateBucketCountFor,
  intervalSeconds,
  planRawFetch,
  RawFetchPlan,
  shouldProbeForRawFetch,
} from 'features/timeSeries/utils/rawFetchPlan'
import { useMemo } from 'react'
import { ChartTimeUnit } from 'types'

/**
 * A request cancelled by us, not a real failure — a newer request superseded it.
 *
 * The single source of truth for this, because the variants are easy to miss:
 * `fetch`/DOMException aborts surface as `AbortError`, axios surfaces its own
 * `CanceledError`, our manual cancel throws `Error('Request cancelled')`, and an
 * `AbortController.abort()` with no reason yields the message "signal is aborted
 * without reason" (which is what leaked through a narrower downstream check and
 * got treated as a genuine failure — briefly flashing "No data" mid-load).
 */
export const isAbortError = (error: unknown): boolean =>
  error instanceof Error &&
  (error.name === 'AbortError' ||
    error.name === 'CanceledError' ||
    error.message === 'Request cancelled' ||
    error.message.includes('aborted without reason'))

const isAbort = isAbortError

export interface TimeSeriesDataPoint {
  entry_id: string
  measurement: string
  timestamp: string
  value: number | null
}

export interface TimeSeriesResponse {
  [key: string]: TimeSeriesDataPoint[]
}

export enum AggregationOp {
  MEAN = 'avg',
  SUM = 'sum',
  MIN = 'min',
  MAX = 'max',
  COUNT = 'count',
}

export interface TimeSeriesRequest {
  entry_ids: string[]
  start_datetime: string
  end_datetime: string
  interval?: number
  interval_unit?: ChartTimeUnit
  aggregation_op?: AggregationOp
  interpolate?: boolean
}

export interface FetchTimeSeriesOptions {
  signal?: AbortSignal
  /** Handle from perfBeginFetch. Omitted outside the perf harness. */
  perfSeq?: number
  /**
   * Fetch pens at their real sample times where that is affordable, instead of
   * bucketing every pen (see `utils/rawFetchPlan.ts`).
   *
   * Off by default. The aggregate endpoint's bucket-start timestamps are load
   * bearing for callers that assume every pen shares one timestamp grid, and
   * this service is shared with the alerts and knowledge-document charts, so
   * only the Process Data page opts in.
   */
  exactTimestamps?: boolean
}

export interface StreamingOptions {
  onProgress?: (progress: {
    bytesProcessed: number
    totalBytes?: number
    itemsProcessed: number
  }) => void
  onChunk?: (data: TimeSeriesResponse) => void
  chunkSize?: number
  maxBufferSize?: number
  signal?: AbortSignal
  /** Handle from perfBeginFetch. Omitted outside the perf harness. */
  perfSeq?: number
  /** See `FetchTimeSeriesOptions.exactTimestamps`. */
  exactTimestamps?: boolean
}

// Temporary: Use dev time series endpoint until production endpoint is ready

export class TimeSeriesService {
  private axios: AuthedAxiosRequests
  private readonly TIME_SERIES_BASE_URL: string

  constructor(axios: AuthedAxiosRequests, timeSeriesBaseUrl: string) {
    this.axios = axios
    this.TIME_SERIES_BASE_URL = timeSeriesBaseUrl
  }

  static getInterval(
    seconds: number,
    max_data_points: number = 10000
  ): {
    interval: number
    interval_unit: ChartTimeUnit
  } {
    // Calculate the required interval in seconds to achieve max_data_points
    const requiredIntervalSeconds = seconds / max_data_points

    if (requiredIntervalSeconds <= 1) {
      return { interval: 1, interval_unit: ChartTimeUnit.SECONDS }
    } else if (requiredIntervalSeconds < 60) {
      return {
        interval: Math.max(1, Math.round(requiredIntervalSeconds)),
        interval_unit: ChartTimeUnit.SECONDS,
      }
    } else if (requiredIntervalSeconds < 3600) {
      const intervalMinutes = Math.max(
        1,
        Math.round(requiredIntervalSeconds / 60)
      )
      return { interval: intervalMinutes, interval_unit: ChartTimeUnit.MINUTES }
    } else if (requiredIntervalSeconds < 86400) {
      const intervalHours = Math.max(
        1,
        Math.round(requiredIntervalSeconds / 3600)
      )
      return { interval: intervalHours, interval_unit: ChartTimeUnit.HOURS }
    } else {
      // Convert to days for larger intervals
      const intervalDays = Math.max(
        1,
        Math.round(requiredIntervalSeconds / 86400)
      )
      return { interval: intervalDays, interval_unit: ChartTimeUnit.DAYS }
    }
  }

  /**
   * How many buckets a bucketed fetch may ask for per pen: an even share of
   * BUCKETED_FETCH_BUDGET.TOTAL_POINTS, clamped so a small chart keeps the
   * full 10,000-bucket default and a crowded one never ships fewer buckets
   * per pen than the client resampler keeps anyway. The pen count is
   * quantized up to the next PEN_COUNT_QUANTUM so small pen-count changes
   * never flip the interval (and the fetch cache). See BUCKETED_FETCH_BUDGET
   * in config/constants.ts for the reasoning.
   *
   * `windowSeconds`, when given, composes a window-size cap on top: past
   * LARGE_WINDOW_SECONDS the per-pen budget drops to
   * LARGE_WINDOW_MAX_POINTS_PER_PEN (= what the resampler keeps), because on
   * huge windows the extra buckets never reach a pixel. The cap is a constant,
   * so quantization — and therefore cache-key stability across pen changes —
   * is untouched.
   */
  static maxDataPointsForPenCount(
    penCount: number,
    windowSeconds?: number
  ): number {
    const quantizedPenCount =
      Math.ceil(
        Math.max(1, penCount) / BUCKETED_FETCH_BUDGET.PEN_COUNT_QUANTUM
      ) * BUCKETED_FETCH_BUDGET.PEN_COUNT_QUANTUM
    const share = Math.floor(
      BUCKETED_FETCH_BUDGET.TOTAL_POINTS / quantizedPenCount
    )
    const perPenCeiling =
      windowSeconds !== undefined &&
      windowSeconds > BUCKETED_FETCH_BUDGET.LARGE_WINDOW_SECONDS
        ? BUCKETED_FETCH_BUDGET.LARGE_WINDOW_MAX_POINTS_PER_PEN
        : BUCKETED_FETCH_BUDGET.MAX_POINTS_PER_PEN
    return Math.min(
      perPenCeiling,
      Math.max(BUCKETED_FETCH_BUDGET.MIN_POINTS_PER_PEN, share)
    )
  }

  /**
   * The interval for the chart's main time range: `getInterval` with the
   * pen-scaled bucket budget instead of the raw 10,000-point default.
   *
   * Every path that bakes an interval into the chart's `TimeRange` (zoom, box
   * zoom, pan/zoom arrows, the range picker, and the interval backfill
   * effects) MUST route through this helper with the chart's current pen
   * count, and `buildTimeSeriesRequest` computes its fallback interval the
   * same way. If any of them disagree, the backfill rewrites the range with a
   * different interval than the first fetch used and the same window is
   * fetched twice.
   */
  static getIntervalForPenCount(
    seconds: number,
    penCount: number
  ): {
    interval: number
    interval_unit: ChartTimeUnit
  } {
    return TimeSeriesService.getInterval(
      seconds,
      TimeSeriesService.maxDataPointsForPenCount(penCount, seconds)
    )
  }

  /**
   * The coarse first pass of a two-phase load for `request`, or null when the
   * window does not warrant one (see TWO_PHASE_LOAD in config/constants.ts).
   *
   * Same window, same pens, same semantics — only the interval is swapped for
   * one targeting ~COARSE_POINTS_PER_PEN buckets/pen, so the result paints a
   * legible line in a fraction of the full fetch's bytes while the full
   * (adaptive-capped) resolution loads behind it. Null is also returned when
   * the coarse interval would not actually be coarser than what the request
   * already asks for: refining to the same resolution is a pointless second
   * fetch.
   */
  static buildCoarsePhaseRequest(
    request: TimeSeriesRequest
  ): TimeSeriesRequest | null {
    const windowSeconds =
      (Date.parse(request.end_datetime) - Date.parse(request.start_datetime)) /
      1000
    if (
      !Number.isFinite(windowSeconds) ||
      windowSeconds <= TWO_PHASE_LOAD.MIN_WINDOW_SECONDS
    ) {
      return null
    }

    const coarse = TimeSeriesService.getInterval(
      windowSeconds,
      TWO_PHASE_LOAD.COARSE_POINTS_PER_PEN
    )

    const coarseSeconds = intervalSeconds(coarse.interval, coarse.interval_unit)
    const requestedSeconds = intervalSeconds(
      request.interval ?? 1,
      request.interval_unit ?? ChartTimeUnit.SECONDS
    )
    if (
      coarseSeconds === null ||
      requestedSeconds === null ||
      coarseSeconds <= requestedSeconds
    ) {
      return null
    }

    return {
      ...request,
      interval: coarse.interval,
      interval_unit: coarse.interval_unit,
    }
  }

  /**
   * Fetches time series data with streaming support for large datasets
   * Processes data incrementally to avoid memory issues with large responses
   */
  async fetchTimeSeriesDataStream(
    request: TimeSeriesRequest,
    options?: StreamingOptions
  ): Promise<TimeSeriesResponse> {
    perfFetchDispatched(options?.perfSeq ?? -1)

    const plan = options?.exactTimestamps
      ? await this.planExactTimestampFetch(request, options)
      : null

    // Nothing qualified for raw (or the probe could not answer): this is the
    // original single-request path, unchanged.
    if (!plan || plan.rawEntryIds.length === 0) {
      const { response, decodedBytes } = await this.fetchAggregate(
        request,
        options
      )
      perfFetchLastByte(options?.perfSeq ?? -1, decodedBytes)
      return response
    }

    // Both halves describe disjoint pens, so they can fly together and be
    // stitched back into the single response shape the caller expects.
    const [raw, aggregated] = await Promise.all([
      this.fetchRaw({ ...request, entry_ids: plan.rawEntryIds }, options),
      plan.aggregateEntryIds.length > 0
        ? this.fetchAggregate(
            { ...request, entry_ids: plan.aggregateEntryIds },
            options
          )
        : Promise.resolve({
            response: {} as TimeSeriesResponse,
            decodedBytes: 0,
          }),
    ])

    perfFetchLastByte(
      options?.perfSeq ?? -1,
      raw.decodedBytes + aggregated.decodedBytes
    )

    return { ...aggregated.response, ...raw.response }
  }

  /**
   * Ask the server how many real samples each pen has in this window, then work
   * out which pens are affordable to fetch raw.
   *
   * Returns null if anything is missing or goes wrong. Exact timestamps are an
   * improvement, not a requirement, so every failure here falls back to the
   * bucketed path rather than surfacing an error.
   */
  private async planExactTimestampFetch(
    request: TimeSeriesRequest,
    options?: StreamingOptions
  ): Promise<RawFetchPlan | null> {
    // `interpolate` asks the server to invent values on a regular grid; raw
    // samples are the opposite request, so the two cannot be mixed.
    if (request.interpolate) return null
    // Only a plain average is equivalent to "the samples themselves". A caller
    // asking for min/max/sum/count wants the reduction.
    if (request.aggregation_op && request.aggregation_op !== AggregationOp.MEAN)
      return null
    if (request.entry_ids.length === 0) return null

    const aggregateBucketCount = aggregateBucketCountFor(
      request.start_datetime,
      request.end_datetime,
      request.interval ?? 1,
      request.interval_unit ?? ChartTimeUnit.SECONDS
    )
    if (aggregateBucketCount === null) return null

    // A long window collapses the per-pen raw ceiling (see
    // shouldProbeForRawFetch): when no pen could qualify anyway, the probe's
    // serialized round trip is pure latency, so skip straight to the bucketed
    // path -- the same outcome as a probe that admitted nobody. A zoom back
    // into a small window passes this check again and probes as before.
    const windowSeconds =
      (Date.parse(request.end_datetime) - Date.parse(request.start_datetime)) /
      1000
    if (!shouldProbeForRawFetch(windowSeconds, aggregateBucketCount)) {
      return null
    }

    try {
      const sampleCounts = await this.countSamplesPerEntry(request, options)
      return planRawFetch({
        entryIds: request.entry_ids,
        sampleCounts,
        aggregateBucketCount,
      })
    } catch (error) {
      if (isAbort(error)) throw error
      console.warn(
        'Sample-count probe failed; falling back to bucketed timestamps:',
        error
      )
      return null
    }
  }

  /**
   * Exact sample count per pen over the request's window.
   *
   * One bucket spanning the whole window would be ideal, but ClickHouse's
   * `toStartOfInterval` snaps buckets to an absolute grid rather than to the
   * window start, so a window-sized interval still lands on a boundary and
   * returns two partial buckets. Summing them is exact regardless (verified
   * against full raw fetches: 4,948 / 547 / 192,201 counted vs 4,948 / 547 /
   * 192,274 actually returned, the drift being live samples arriving between the
   * two calls).
   */
  private async countSamplesPerEntry(
    request: TimeSeriesRequest,
    options?: StreamingOptions
  ): Promise<Record<string, number>> {
    const windowSeconds = Math.max(
      1,
      Math.ceil(
        (Date.parse(request.end_datetime) -
          Date.parse(request.start_datetime)) /
          1000
      )
    )

    const { response } = await this.fetchAggregate(
      {
        entry_ids: request.entry_ids,
        start_datetime: request.start_datetime,
        end_datetime: request.end_datetime,
        interval: windowSeconds,
        interval_unit: ChartTimeUnit.SECONDS,
        aggregation_op: AggregationOp.COUNT,
      },
      // The probe is a separate request; it must not report its own bytes or
      // first byte against the fetch record.
      { signal: options?.signal }
    )

    const counts: Record<string, number> = {}
    for (const [entryId, points] of Object.entries(response)) {
      counts[entryId] = points.reduce(
        (total, point) => total + (point.value ?? 0),
        0
      )
    }
    return counts
  }

  private fetchAggregate(
    request: TimeSeriesRequest,
    options?: StreamingOptions
  ): Promise<{ response: TimeSeriesResponse; decodedBytes: number }> {
    return this.fetchStream(
      `${this.TIME_SERIES_BASE_URL}/api/v2/time-series/read/aggregate`,
      {
        ...request,
        interval: request.interval || 1,
        interval_unit: request.interval_unit || ChartTimeUnit.SECONDS,
        aggregation_op: request.aggregation_op || AggregationOp.MEAN,
        gap_fill: request.interpolate ? 'interpolate' : 'include_nulls',
      },
      options
    )
  }

  /**
   * The pens we can afford to have the truth about. No interval, no aggregation
   * op and no gap fill: this endpoint returns the stored samples, at the instants
   * they were stored.
   */
  private fetchRaw(
    request: TimeSeriesRequest,
    options?: StreamingOptions
  ): Promise<{ response: TimeSeriesResponse; decodedBytes: number }> {
    return this.fetchStream(
      `${this.TIME_SERIES_BASE_URL}/api/v2/time-series/read`,
      {
        entry_ids: request.entry_ids,
        start_datetime: request.start_datetime,
        end_datetime: request.end_datetime,
      },
      options
    )
  }

  private async fetchStream(
    url: string,
    requestBody: object,
    options?: StreamingOptions
  ): Promise<{ response: TimeSeriesResponse; decodedBytes: number }> {
    const {
      onProgress,
      onChunk,
      chunkSize = 65536, // 64KB default chunk size
      maxBufferSize = 10 * 1024 * 1024, // 10MB max buffer
      signal,
      perfSeq,
    } = options || {}

    try {
      const response = await this.axios.post(url, requestBody, {
        adapter: 'fetch',
        responseType: 'stream',
        signal,
        onDownloadProgress: (progressEvent) => {
          if (onProgress && progressEvent.loaded) {
            onProgress({
              bytesProcessed: progressEvent.loaded,
              totalBytes: progressEvent.total,
              itemsProcessed: 0, // Will be updated during parsing
            })
          }
        },
      })

      if (!response.data) {
        throw new Error('Empty response from time series API')
      }

      return await this.processStreamResponse(response.data, {
        onProgress,
        onChunk,
        chunkSize,
        maxBufferSize,
        signal,
        perfSeq,
      })
    } catch (error) {
      if (isAbort(error)) {
        throw error
      }
      console.error('Error fetching streaming time series data:', error)
      throw new Error(
        `Time series streaming API failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  /**
   * Process streaming response data incrementally.
   *
   * Reports its own decoded byte count rather than recording it against the perf
   * fetch record: one logical fetch can now be several HTTP requests, and only
   * the caller knows when they have all landed.
   */
  private async processStreamResponse(
    stream: ReadableStream | string,
    options: StreamingOptions
  ): Promise<{ response: TimeSeriesResponse; decodedBytes: number }> {
    const {
      onProgress,
      onChunk,
      maxBufferSize = 10 * 1024 * 1024,
      perfSeq = -1,
    } = options

    if (typeof stream === 'string') {
      // Non-streaming fallback: the whole body already arrived, so first and
      // last byte coincide.
      perfFetchFirstByte(perfSeq)
      return {
        response: this.parseTimeSeriesData(stream),
        decodedBytes: stream.length,
      }
    }

    const reader =
      stream instanceof ReadableStream
        ? stream.getReader()
        : this.createReaderFromData(stream)

    const decoder = new TextDecoder('utf-8')
    let buffer = ''
    let bytesProcessed = 0
    let itemsProcessed = 0
    const result: TimeSeriesResponse = {}

    try {
      while (true) {
        const { done, value } = await reader.read()

        if (done) break

        perfFetchFirstByte(perfSeq)

        const chunk = decoder.decode(value, { stream: true })
        buffer += chunk
        bytesProcessed += value.byteLength

        if (buffer.length > maxBufferSize) {
          throw new Error(
            `Buffer size exceeded maximum of ${maxBufferSize} bytes`
          )
        }

        // Process complete lines (NDJSON format)
        const lines = buffer.split('\n')
        buffer = lines.pop() || '' // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.trim()) continue

          try {
            const parsed = JSON.parse(line) as Record<
              string,
              TimeSeriesDataPoint[]
            >

            // Merge parsed data into result
            Object.entries(parsed).forEach(([key, points]) => {
              if (!result[key]) {
                result[key] = []
              }
              result[key].push(...points)
              itemsProcessed += points.length
            })

            // Call chunk callback if provided
            if (onChunk) {
              onChunk(parsed)
            }

            // Update progress
            if (onProgress) {
              onProgress({
                bytesProcessed,
                itemsProcessed,
                totalBytes: undefined,
              })
            }
          } catch (parseError) {
            // Log but continue processing other lines
            console.warn('Failed to parse line:', line, parseError)
          }
        }
      }

      // Process any remaining data in buffer
      if (buffer.trim()) {
        try {
          const parsed = JSON.parse(buffer) as Record<
            string,
            TimeSeriesDataPoint[]
          >
          Object.entries(parsed).forEach(([key, points]) => {
            if (!result[key]) {
              result[key] = []
            }
            result[key].push(...points)
            itemsProcessed += points.length
          })
        } catch (parseError) {
          console.warn('Failed to parse final buffer:', buffer, parseError)
        }
      }
      return { response: result, decodedBytes: bytesProcessed }
    } finally {
      reader.releaseLock?.()
    }
  }

  /**
   * Create a reader from various data types
   */
  private createReaderFromData(data: unknown): ReadableStreamDefaultReader {
    let stream: ReadableStream

    if (data instanceof ReadableStream) {
      stream = data
    } else if (
      typeof data === 'object' &&
      data !== null &&
      'getReader' in data
    ) {
      stream = data as ReadableStream
    } else {
      const encoder = new TextEncoder()
      const uint8Array = encoder.encode(String(data))
      stream = new ReadableStream({
        start(controller) {
          controller.enqueue(uint8Array)
          controller.close()
        },
      })
    }

    return stream.getReader()
  }

  /**
   * Parse time series data from string (fallback for non-streaming)
   */
  private parseTimeSeriesData(data: string): TimeSeriesResponse {
    // Try standard JSON first
    try {
      return JSON.parse(data) as TimeSeriesResponse
    } catch {
      // Try NDJSON format
      return this.parseNDJSON(data)
    }
  }

  /**
   * Parse NDJSON (newline-delimited JSON) format
   */
  private parseNDJSON(data: string): TimeSeriesResponse {
    const lines = data.trim().split('\n')
    const result: TimeSeriesResponse = {}

    for (const line of lines) {
      if (!line.trim()) continue

      try {
        const parsed = JSON.parse(line) as Record<string, TimeSeriesDataPoint[]>
        Object.entries(parsed).forEach(([key, points]) => {
          if (!result[key]) {
            result[key] = []
          }
          result[key].push(...points)
        })
      } catch (error) {
        console.warn('Failed to parse NDJSON line:', line, error)
      }
    }

    if (Object.keys(result).length === 0) {
      throw new Error('No valid data parsed from NDJSON response')
    }

    return result
  }

  /**
   * Legacy method - now uses streaming internally but maintains same interface
   */
  async fetchTimeSeriesData(
    request: TimeSeriesRequest,
    options?: FetchTimeSeriesOptions
  ): Promise<TimeSeriesResponse> {
    // Use streaming method internally for better performance
    return this.fetchTimeSeriesDataStream(request, {
      signal: options?.signal,
      perfSeq: options?.perfSeq,
      exactTimestamps: options?.exactTimestamps,
    })
  }

  /**
   * Fetch latest sensor values for the given sensors
   * @param sensorIds Array of sensor IDs to fetch values for
   * @returns Object mapping sensor IDs to their latest values
   */
  async fetchLatestSensorValues(
    sensorIds: string[]
  ): Promise<Record<string, TimeSeriesDataPoint | null>> {
    if (!sensorIds || sensorIds.length === 0) {
      return {}
    }
    const requestBody = {
      entry_ids: sensorIds,
      count: 3,
    }

    try {
      const response = await this.axios.post(
        `${this.TIME_SERIES_BASE_URL}/api/v2/time-series/read/latest`,
        requestBody
      )
      if (!response.data) {
        throw new Error('Empty response from time series API')
      }
      // go from array to object mapping sensorId to data point or null
      const result: Record<string, TimeSeriesDataPoint | null> = {}
      const data = response.data as Record<string, TimeSeriesDataPoint[] | null>

      sensorIds.forEach((id) => {
        if (data[id] && data[id].length > 0) {
          // Find the latest data point by timestamp
          result[id] = data[id].at(-1)!
        } else {
          result[id] = null
        }
      })
      return result
    } catch (error) {
      console.error('Error fetching streaming time series data:', error)
      throw new Error(
        `Time series streaming API failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  /**
   * Earliest and latest timestamp at which ANY of `sensorIds` has data inside
   * `[startIso, endIso)`. Used to point an operator at where data actually is
   * when the window they are looking at is empty.
   *
   * `gap_fill: 'off'` is the whole trick: the server then returns only buckets
   * that contain data (empty buckets are omitted, per the ClickHouse query
   * builder), so the boundary is simply the min/max timestamp of the response —
   * no client-side histogram scan. `count` rather than `avg` because we only
   * care where data exists, not its value, and count skips value processing.
   *
   * Coarse by design (~500 buckets across the window): a day-level answer is
   * plenty to say "data resumes around <date>" and to seed a jump target.
   */
  async fetchDataPresence(
    sensorIds: string[],
    startIso: string,
    endIso: string
  ): Promise<{ earliest: string | null; latest: string | null }> {
    if (!sensorIds || sensorIds.length === 0) {
      return { earliest: null, latest: null }
    }

    const spanSeconds = (Date.parse(endIso) - Date.parse(startIso)) / 1000
    if (!Number.isFinite(spanSeconds) || spanSeconds <= 0) {
      return { earliest: null, latest: null }
    }
    const { interval, interval_unit } = TimeSeriesService.getInterval(
      spanSeconds,
      500
    )

    const requestBody = {
      entry_ids: sensorIds,
      start_datetime: startIso,
      end_datetime: endIso,
      interval,
      interval_unit,
      aggregation_op: AggregationOp.COUNT,
      gap_fill: 'off',
    }

    const response = await this.axios.post(
      `${this.TIME_SERIES_BASE_URL}/api/v2/time-series/read/aggregate`,
      requestBody
    )

    return extractPresenceBounds(response.data)
  }
}

/**
 * Min/max timestamp across a `read/aggregate` response, tolerant of the shapes
 * the endpoint can hand back: a parsed `{ entry_id: rows[] }` object, a single
 * NDJSON string, or already-joined text. Only timestamps matter here.
 */
export const extractPresenceBounds = (
  data: unknown
): { earliest: string | null; latest: string | null } => {
  let earliest: number | null = null
  let latest: number | null = null

  const consider = (timestamp: unknown) => {
    if (typeof timestamp !== 'string') return
    const ms = Date.parse(timestamp)
    if (Number.isNaN(ms)) return
    if (earliest === null || ms < earliest) earliest = ms
    if (latest === null || ms > latest) latest = ms
  }

  const walkGrouped = (grouped: Record<string, unknown>) => {
    Object.values(grouped).forEach((rows) => {
      if (!Array.isArray(rows)) return
      rows.forEach((row) => {
        if (row && typeof row === 'object' && 'timestamp' in row) {
          consider((row as { timestamp: unknown }).timestamp)
        }
      })
    })
  }

  if (typeof data === 'string') {
    data
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        try {
          walkGrouped(JSON.parse(line) as Record<string, unknown>)
        } catch {
          // A partial or non-JSON line tells us nothing; skip it.
        }
      })
  } else if (data && typeof data === 'object') {
    walkGrouped(data as Record<string, unknown>)
  }

  return {
    earliest: earliest === null ? null : new Date(earliest).toISOString(),
    latest: latest === null ? null : new Date(latest).toISOString(),
  }
}

export const useTimeSeriesService = () => {
  const { authorizedAxios } = useGeckoAuth()
  const { TIME_SERIES_BASE_URL } = useRuntimeVariables()
  return useMemo(() => {
    const service = new TimeSeriesService(authorizedAxios, TIME_SERIES_BASE_URL)
    return {
      fetchTimeSeriesData: service.fetchTimeSeriesData.bind(service),
      fetchTimeSeriesDataStream:
        service.fetchTimeSeriesDataStream.bind(service),
      fetchLatestSensorValues: service.fetchLatestSensorValues.bind(service),
      fetchDataPresence: service.fetchDataPresence.bind(service),
    }
  }, [TIME_SERIES_BASE_URL, authorizedAxios])
}
